import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Shield, ShieldAlert, ShieldCheck, Unlock, Loader2, Clock, MapPin, AlertTriangle, CheckCircle2, XCircle, Activity } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminSecurityTab() {
  const [logs, setLogs] = useState([]);
  const [blockedAccounts, setBlockedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState(false);
  const [unblockingEmail, setUnblockingEmail] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [logsRes, blockedRes] = await Promise.all([
        axios.get(`${API}/admin/security-logs`, { headers }).catch(() => ({ data: { logs: [] } })),
        axios.get(`${API}/auth/blocked-accounts`, { headers }).catch(() => ({ data: { blocked_accounts: [] } })),
      ]);
      setLogs(logsRes.data.logs || []);
      setBlockedAccounts(blockedRes.data.blocked_accounts || []);
    } catch { setLogs([]); setBlockedAccounts([]); }
    finally { setLoading(false); }
  };

  const handleUnblockAll = async () => {
    if (!window.confirm('Unblock semua akaun yang diblock?')) return;
    setUnblocking(true);
    try {
      await axios.post(`${API}/auth/unblock-ip`, {}, { headers });
      toast.success('Semua akaun telah diunblock');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal unblock'); }
    finally { setUnblocking(false); }
  };

  const handleUnblockEmail = async (email) => {
    setUnblockingEmail(email);
    try {
      await axios.post(`${API}/auth/unblock-email`, { email }, { headers });
      toast.success(`${email} telah diunblock`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal unblock'); }
    finally { setUnblockingEmail(null); }
  };

  const blockedEvents = logs.filter(l => l.type === 'brute_force_blocked');
  const successLogins = logs.filter(l => l.type === 'login_success');
  const failedAttempts = logs.filter(l => l.type === 'brute_force_blocked');
  const uniqueIPs = new Set(logs.map(l => l.ip)).size;
  const last24h = logs.filter(l => new Date(l.created_at) > new Date(Date.now() - 86400000));
  const blockedIPs = [...new Set(blockedEvents.map(l => l.ip))];

  // Activity by hour (last 24h)
  const hourlyData = Array(24).fill(0);
  last24h.forEach(l => {
    const hour = new Date(l.created_at).getHours();
    hourlyData[hour]++;
  });
  const maxHourly = Math.max(...hourlyData, 1);

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin text-[#E63946]" /></div>;

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/5 border border-emerald-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><ShieldCheck size={18} className="text-emerald-400" /><span className="text-xs text-[hsl(var(--text-muted))]">Successful Logins</span></div>
          <p className="text-3xl font-bold text-white">{successLogins.length}</p>
          <p className="text-[10px] text-emerald-400 mt-1">{last24h.filter(l => l.type === 'login_success').length} in last 24h</p>
        </div>
        <div className="bg-gradient-to-br from-red-500/10 to-rose-500/5 border border-red-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><ShieldAlert size={18} className="text-red-400" /><span className="text-xs text-[hsl(var(--text-muted))]">Blocked Attempts</span></div>
          <p className="text-3xl font-bold text-white">{blockedEvents.length}</p>
          <p className="text-[10px] text-red-400 mt-1">{blockedIPs.length} unique IPs blocked</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/5 border border-blue-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><MapPin size={18} className="text-blue-400" /><span className="text-xs text-[hsl(var(--text-muted))]">Unique IPs</span></div>
          <p className="text-3xl font-bold text-white">{uniqueIPs}</p>
          <p className="text-[10px] text-blue-400 mt-1">Total tracked IPs</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><Activity size={18} className="text-amber-400" /><span className="text-xs text-[hsl(var(--text-muted))]">Last 24h Events</span></div>
          <p className="text-3xl font-bold text-white">{last24h.length}</p>
          <p className="text-[10px] text-amber-400 mt-1">Security events</p>
        </div>
      </div>

      {/* 24h Activity Chart */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2"><Clock size={16} className="text-[#E63946]" /> Login Activity (Last 24 Hours)</h3>
        <div className="flex items-end gap-1 h-20">
          {hourlyData.map((count, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full rounded-t transition-all" style={{ height: `${(count / maxHourly) * 60}px`, minHeight: count > 0 ? '4px' : '1px', backgroundColor: count > 3 ? '#EF4444' : count > 0 ? '#E63946' : 'rgba(255,255,255,0.05)' }} />
              {i % 3 === 0 && <span className="text-[8px] text-[hsl(var(--text-muted))]">{String(i).padStart(2, '0')}</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Blocked Accounts - Individual Unblock */}
      {blockedAccounts.length > 0 && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-red-400 flex items-center gap-2" data-testid="blocked-accounts-title">
              <AlertTriangle size={16} /> Akaun Diblock ({blockedAccounts.length})
            </h3>
            <Button size="sm" onClick={handleUnblockAll} disabled={unblocking} className="bg-red-600 text-white h-8" data-testid="unblock-all-btn">
              {unblocking ? <Loader2 size={14} className="animate-spin mr-1" /> : <Unlock size={14} className="mr-1" />} Unblock Semua
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {blockedAccounts.map((account) => (
              <div key={account.email} className="flex items-center gap-3 p-3 bg-red-500/10 rounded-lg border border-red-500/15" data-testid={`blocked-account-${account.email}`}>
                <XCircle size={16} className="text-red-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{account.email}</p>
                  <p className="text-[10px] text-red-300/60">
                    IP: {account.ip || '?'} | {account.attempts || '?'} cubaan
                    {account.blocked_at && ` | ${new Date(account.blocked_at).toLocaleString('en-MY', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`}
                  </p>
                </div>
                <Button 
                  size="sm" 
                  onClick={() => handleUnblockEmail(account.email)} 
                  disabled={unblockingEmail === account.email}
                  className="bg-amber-600 hover:bg-amber-500 text-white h-7 px-3 text-xs flex-shrink-0"
                  data-testid={`unblock-btn-${account.email}`}
                >
                  {unblockingEmail === account.email ? <Loader2 size={12} className="animate-spin" /> : <Unlock size={12} className="mr-1" />}
                  Unblock
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Blocked IPs from Security Logs (historical) */}
      {blockedIPs.length > 0 && blockedAccounts.length === 0 && (
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-[hsl(var(--text-muted))] flex items-center gap-2 mb-3">
            <ShieldCheck size={16} className="text-emerald-400" /> Tiada akaun yang sedang diblock
          </h3>
          <p className="text-xs text-[hsl(var(--text-muted))]">Semua akaun boleh login. Sejarah block boleh dilihat dalam Security Event Timeline di bawah.</p>
        </div>
      )}

      {/* Security Log Timeline */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2"><Shield size={16} className="text-[#E63946]" /> Security Event Timeline</h3>
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {logs.length === 0 && <p className="text-center text-[hsl(var(--text-muted))] py-8">No security events recorded</p>}
          {[...logs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((log) => (
            <div key={log.id} className={`flex items-center gap-3 p-3 rounded-lg ${log.type === 'brute_force_blocked' ? 'bg-red-500/5 border border-red-500/10' : 'bg-emerald-500/5 border border-emerald-500/10'}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${log.type === 'brute_force_blocked' ? 'bg-red-500/20' : 'bg-emerald-500/20'}`}>
                {log.type === 'brute_force_blocked' ? <ShieldAlert size={14} className="text-red-400" /> : <CheckCircle2 size={14} className="text-emerald-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-white font-medium">{log.email || 'Unknown'}</span>
                  <Badge className={`text-[9px] ${log.type === 'brute_force_blocked' ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                    {log.type === 'brute_force_blocked' ? 'BLOCKED' : 'SUCCESS'}
                  </Badge>
                </div>
                <p className="text-[10px] text-[hsl(var(--text-muted))]">
                  IP: {log.ip} {log.attempts ? `| ${log.attempts} attempts` : ''}
                </p>
              </div>
              <div className="text-[10px] text-[hsl(var(--text-muted))] text-right flex-shrink-0">
                {new Date(log.created_at).toLocaleString('en-MY', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
